import React, { useState, useEffect } from 'react';

const NotificationCenter = () => {
  // Load notifications from localStorage
  const loadNotifications = () => {
    try {
      const savedNotifications = localStorage.getItem('webos-notifications');
      if (savedNotifications) {
        return JSON.parse(savedNotifications);
      }
    } catch (e) {
      console.error('Failed to load notifications:', e);
    }
    return [];
  };

  const [notifications, setNotifications] = useState(loadNotifications);
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Save notifications to localStorage when they change
  useEffect(() => {
    localStorage.setItem('webos-notifications', JSON.stringify(notifications));
    
    // Calculate unread count
    const unread = notifications.filter(notification => !notification.read).length;
    setUnreadCount(unread);
  }, [notifications]);

  // Register global notification handler
  useEffect(() => {
    // Create a function that adds a new notification
    window.addNotification = (notification) => {
      const newNotification = {
        id: Date.now(),
        timestamp: new Date().toISOString(),
        read: false,
        ...notification
      };
      
      setNotifications(prevNotifications => [newNotification, ...prevNotifications]);
      
      // Show notification as a toast
      showToast(newNotification);
      
      return newNotification.id;
    };
    
    // Add demo notification if first time
    const hasSeenWelcome = localStorage.getItem('webos-notifications-welcome');
    if (!hasSeenWelcome && notifications.length === 0) {
      window.addNotification({
        title: 'Welcome to WebOS',
        message: 'This is your notification center. System alerts and app notifications will appear here.',
        icon: 'info-circle',
        type: 'info'
      });
      localStorage.setItem('webos-notifications-welcome', 'true');
    }
    
    return () => {
      delete window.addNotification;
    };
  }, [notifications.length]);

  const showToast = (notification) => {
    // Create toast element
    const toast = document.createElement('div');
    toast.className = 'notification-toast';
    toast.style.position = 'fixed';
    toast.style.bottom = '20px';
    toast.style.right = '20px';
    toast.style.width = '300px';
    toast.style.padding = '12px';
    toast.style.backgroundColor = 'rgba(30, 30, 50, 0.8)';
    toast.style.backdropFilter = 'blur(10px)';
    toast.style.color = '#fff';
    toast.style.borderRadius = '10px';
    toast.style.boxShadow = '0 5px 15px rgba(0, 0, 0, 0.3)';
    toast.style.zIndex = '10000';
    toast.style.transition = 'all 0.3s ease';
    toast.style.transform = 'translateY(20px)';
    toast.style.opacity = '0';
    
    // Add notification content
    toast.innerHTML = `
      <div style="display: flex; align-items: center; margin-bottom: 8px;">
        <i class="fas fa-${notification.icon || 'bell'}" style="margin-right: 8px; color: ${getTypeColor(notification.type)}"></i>
        <div style="font-weight: bold;">${notification.title}</div>
        <div style="margin-left: auto; font-size: 12px; opacity: 0.7;">
          ${formatTimestamp(notification.timestamp)}
        </div>
      </div>
      <div style="margin-left: 24px;">${notification.message}</div>
    `;
    
    // Add to document
    document.body.appendChild(toast);
    
    // Trigger animation
    setTimeout(() => {
      toast.style.transform = 'translateY(0)';
      toast.style.opacity = '1';
    }, 10);
    
    // Remove after 5 seconds
    setTimeout(() => {
      toast.style.transform = 'translateY(20px)';
      toast.style.opacity = '0';
      setTimeout(() => {
        document.body.removeChild(toast);
      }, 300);
    }, 5000);
  };

  const toggleNotificationCenter = () => {
    setIsOpen(!isOpen);
    
    if (!isOpen) {
      // Mark all as read when opening
      setNotifications(notifications.map(notification => ({
        ...notification,
        read: true
      })));
    }
  };

  const clearNotifications = () => {
    setNotifications([]);
    localStorage.removeItem('webos-notifications');
  };

  const deleteNotification = (id) => {
    setNotifications(notifications.filter(notification => notification.id !== id));
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'error': return '#ff5555';
      case 'warning': return '#ffb86c';
      case 'success': return '#50fa7b';
      case 'info':
      default: return '#8be9fd';
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    
    return date.toLocaleDateString();
  };

  return (
    <>
      {/* Notification Icon in Taskbar or Header */}
      <div 
        onClick={toggleNotificationCenter}
        style={{
          position: 'relative',
          cursor: 'pointer',
          padding: '8px',
          color: '#fff'
        }}
      >
        <i className="fas fa-bell" style={{ fontSize: '18px' }} />
        {unreadCount > 0 && (
          <div style={{
            position: 'absolute',
            top: '0',
            right: '0',
            backgroundColor: '#ff5555',
            color: 'white',
            borderRadius: '50%',
            width: '16px',
            height: '16px',
            fontSize: '10px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            {unreadCount}
          </div>
        )}
      </div>
      
      {/* Notification Panel */}
      {isOpen && (
        <div style={{
          position: 'absolute',
          bottom: '40px',
          right: '10px',
          width: '350px',
          maxHeight: '500px',
          backgroundColor: 'rgba(20, 20, 35, 0.85)',
          backdropFilter: 'blur(10px)',
          borderRadius: '10px',
          boxShadow: '0 5px 20px rgba(0, 0, 0, 0.3)',
          zIndex: '1000',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          transform: 'translateY(0)',
          animation: 'slideUp 0.2s ease-out',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
        
          <style>
            {`
              @keyframes slideUp {
                from {
                  transform: translateY(20px);
                  opacity: 0;
                }
                to {
                  transform: translateY(0);
                  opacity: 1;
                }
              }
            `}
          </style>
          
          {/* Pointer Triangle */}
          <div style={{
            position: 'absolute',
            bottom: '-8px',
            right: '15px',
            width: '16px',
            height: '8px',
            overflow: 'hidden'
          }}>
            <div style={{
              position: 'absolute',
              top: '-8px',
              left: '0',
              width: '16px',
              height: '16px',
              backgroundColor: 'rgba(20, 20, 35, 0.85)',
              transform: 'rotate(45deg)',
              boxShadow: '0 5px 20px rgba(0, 0, 0, 0.3)',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}></div>
          </div>
          
          {/* Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '10px 15px',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <h3 style={{ margin: 0, color: '#fff' }}>Notifications</h3>
            <button
              onClick={clearNotifications}
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                color: '#fff',
                opacity: 0.7,
                cursor: 'pointer'
              }}
            >
              Clear All
            </button>
          </div>
          
          {/* Notification List */}
          <div style={{
            overflowY: 'auto',
            flexGrow: 1,
            padding: '10px',
            color: '#fff'
          }}>
            {notifications.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '30px 0',
                opacity: 0.7
              }}>
                No notifications
              </div>
            ) : (
              notifications.map(notification => (
                <div 
                  key={notification.id}
                  style={{
                    padding: '10px',
                    marginBottom: '8px',
                    borderRadius: '8px',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    position: 'relative'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
                    <i 
                      className={`fas fa-${notification.icon || 'bell'}`} 
                      style={{ marginRight: '8px', color: getTypeColor(notification.type) }} 
                    />
                    <div style={{ fontWeight: 'bold' }}>{notification.title}</div>
                    <div style={{ marginLeft: 'auto', fontSize: '12px', opacity: 0.7 }}>
                      {formatTimestamp(notification.timestamp)}
                    </div>
                  </div>
                  <div style={{ marginLeft: '24px', fontSize: '14px' }}>
                    {notification.message}
                  </div>
                  <button
                    onClick={() => deleteNotification(notification.id)}
                    style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      background: 'none',
                      border: 'none',
                      color: '#fff',
                      opacity: 0.5,
                      fontSize: '12px',
                      cursor: 'pointer'
                    }}
                  >
                    <i className="fas fa-times" />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default NotificationCenter; 